import { useState, useEffect, useRef, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { MagnifyingGlass, Faders, X, SquaresFour, MapTrifold } from "@phosphor-icons/react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api from "@/lib/api";
import ItemCard from "@/components/ItemCard";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

const CONDITIONS = ["Like New", "Good", "Fair", "Well Used"];
const SORTS = [
  { value: "newest", label: "Newest First" },
  { value: "price_low", label: "Price: Low to High" },
  { value: "price_high", label: "Price: High to Low" },
  { value: "popular", label: "Most Popular" },
];

function MapView({ items }) {
  const mapRef = useRef(null);
  const mapInstance = useRef(null);
  const markersRef = useRef([]);
  const navigate = useNavigate();

  useEffect(() => {
    if (!mapRef.current) return;
    if (!mapInstance.current) {
      mapInstance.current = L.map(mapRef.current).setView([39.8, -98.5], 4);
      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
      }).addTo(mapInstance.current);
    }

    markersRef.current.forEach(m => m.remove());
    markersRef.current = [];

    const geoItems = items.filter(i => i.latitude && i.longitude);
    geoItems.forEach(item => {
      const marker = L.circleMarker([item.latitude, item.longitude], {
        radius: 10, fillColor: "#2C5545", color: "#FDFBF7", weight: 2, fillOpacity: 0.9,
      }).addTo(mapInstance.current);
      const popup = L.popup({ className: "borrow-popup" }).setContent(
        `<div style="font-family:Manrope,sans-serif;padding:4px 0">
          <b style="font-size:13px;color:#1A1C1A">${item.title}</b><br/>
          <span style="color:#2C5545;font-weight:600">$${item.price_per_day?.toFixed(2)}/day</span><br/>
          <span style="color:#5C665D;font-size:11px">${item.location}</span>
        </div>`
      );
      marker.bindPopup(popup);
      marker.on("click", () => { marker.openPopup(); });
      marker.on("dblclick", () => { navigate(`/item/${item.id}`); });
      markersRef.current.push(marker);
    });

    if (geoItems.length > 0) {
      const bounds = L.latLngBounds(geoItems.map(i => [i.latitude, i.longitude]));
      mapInstance.current.fitBounds(bounds, { padding: [50, 50], maxZoom: 12 });
    }

    return () => {};
  }, [items, navigate]);

  useEffect(() => {
    return () => {
      if (mapInstance.current) {
        mapInstance.current.remove();
        mapInstance.current = null;
      }
    };
  }, []);

  return <div ref={mapRef} className="w-full h-[600px] rounded-2xl border border-[#EAE7E0] overflow-hidden" data-testid="browse-map" />;
}

export default function Browse() {
  const [params, setParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [viewMode, setViewMode] = useState("grid");

  const search = params.get("search") || "";
  const category = params.get("category") || "";
  const condition = params.get("condition") || "";
  const sort = params.get("sort") || "newest";
  const page = parseInt(params.get("page") || "1");

  useEffect(() => {
    api.get("/categories").then(r => setCategories(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const q = new URLSearchParams();
    if (search) q.set("search", search);
    if (category) q.set("category", category);
    if (condition) q.set("condition", condition);
    q.set("sort", sort);
    q.set("page", page);
    q.set("limit", viewMode === "map" ? 100 : 20);
    api.get(`/items?${q.toString()}`)
      .then(r => { setItems(r.data.items || []); setTotal(r.data.total || 0); setPages(r.data.pages || 1); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [search, category, condition, sort, page, viewMode]);

  const updateParam = (key, val) => {
    const next = new URLSearchParams(params);
    if (val) next.set(key, val); else next.delete(key);
    if (key !== "page") next.set("page", "1");
    setParams(next);
  };

  const clearFilters = () => setParams({});
  const hasFilters = search || category || condition;

  return (
    <main className="min-h-[calc(100vh-4rem)] bg-[#FDFBF7]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Search Bar + View Toggle */}
        <div className="flex gap-3 mb-8">
          <div className="relative flex-1">
            <MagnifyingGlass size={18} weight="duotone" className="absolute left-4 top-1/2 -translate-y-1/2 text-[#5C665D]" />
            <input
              type="text" value={search}
              onChange={e => updateParam("search", e.target.value)}
              placeholder="Search items..."
              className="w-full bg-white border border-[#EAE7E0] rounded-full pl-11 pr-4 py-3 text-sm focus:ring-2 focus:ring-[#2C5545]/20 focus:border-[#2C5545] transition-all outline-none"
              data-testid="browse-search-input"
            />
          </div>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center gap-2 bg-white border border-[#EAE7E0] hover:border-[#2C5545] rounded-full px-5 py-3 text-sm font-medium transition-all"
            data-testid="browse-filter-toggle"
          >
            <Faders size={18} weight="duotone" /> Filters
          </button>
          <div className="flex bg-white border border-[#EAE7E0] rounded-full overflow-hidden">
            <button
              onClick={() => setViewMode("grid")}
              className={`px-3 py-3 transition-all ${viewMode === "grid" ? "bg-[#2C5545] text-white" : "text-[#5C665D] hover:text-[#2C5545]"}`}
              data-testid="browse-grid-view"
            >
              <SquaresFour size={18} weight="duotone" />
            </button>
            <button
              onClick={() => setViewMode("map")}
              className={`px-3 py-3 transition-all ${viewMode === "map" ? "bg-[#2C5545] text-white" : "text-[#5C665D] hover:text-[#2C5545]"}`}
              data-testid="browse-map-view"
            >
              <MapTrifold size={18} weight="duotone" />
            </button>
          </div>
        </div>

        {/* Filters */}
        {showFilters && (
          <div className="bg-white border border-[#EAE7E0] rounded-2xl p-6 mb-8 animate-fade-in grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-semibold text-[#5C665D] uppercase tracking-wider mb-2">Category</label>
              <Select value={category} onValueChange={v => updateParam("category", v === "all" ? "" : v)}>
                <SelectTrigger className="bg-[#FDFBF7] border-[#EAE7E0] rounded-xl" data-testid="browse-category-select">
                  <SelectValue placeholder="All Categories" />
                </SelectTrigger>
                <SelectContent className="bg-white border-[#EAE7E0] rounded-xl">
                  <SelectItem value="all">All Categories</SelectItem>
                  {categories.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-[#5C665D] uppercase tracking-wider mb-2">Condition</label>
              <Select value={condition} onValueChange={v => updateParam("condition", v === "all" ? "" : v)}>
                <SelectTrigger className="bg-[#FDFBF7] border-[#EAE7E0] rounded-xl" data-testid="browse-condition-select">
                  <SelectValue placeholder="Any Condition" />
                </SelectTrigger>
                <SelectContent className="bg-white border-[#EAE7E0] rounded-xl">
                  <SelectItem value="all">Any Condition</SelectItem>
                  {CONDITIONS.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-[#5C665D] uppercase tracking-wider mb-2">Sort By</label>
              <Select value={sort} onValueChange={v => updateParam("sort", v)}>
                <SelectTrigger className="bg-[#FDFBF7] border-[#EAE7E0] rounded-xl" data-testid="browse-sort-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white border-[#EAE7E0] rounded-xl">
                  {SORTS.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {hasFilters && (
              <button onClick={clearFilters} className="sm:col-span-3 text-sm text-[#D96C4A] hover:text-[#C25939] font-medium flex items-center gap-1" data-testid="browse-clear-filters">
                <X size={14} /> Clear all filters
              </button>
            )}
          </div>
        )}

        {/* Results header */}
        <div className="flex items-center justify-between mb-6">
          <p className="text-sm text-[#5C665D]">
            {total} {total === 1 ? "item" : "items"} found
            {viewMode === "map" && <span className="ml-2 text-[#D96C4A]">(showing items with locations on map)</span>}
          </p>
        </div>

        {/* Content */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 border-2 border-[#2C5545] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-20" data-testid="browse-empty-state">
            <p className="font-heading text-2xl text-[#1A1C1A] mb-2">No items found</p>
            <p className="text-sm text-[#5C665D]">Try adjusting your search or filters.</p>
          </div>
        ) : viewMode === "map" ? (
          <MapView items={items} />
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
              {items.map(item => <ItemCard key={item.id} item={item} />)}
            </div>
            {pages > 1 && (
              <div className="flex items-center justify-center gap-2 mt-10">
                {Array.from({ length: Math.min(pages, 10) }, (_, i) => i + 1).map(p => (
                  <button
                    key={p}
                    onClick={() => updateParam("page", String(p))}
                    className={`w-10 h-10 rounded-full text-sm font-medium transition-all ${p === page ? "bg-[#2C5545] text-white" : "bg-white border border-[#EAE7E0] text-[#5C665D] hover:border-[#2C5545]"}`}
                    data-testid={`browse-page-${p}`}
                  >
                    {p}
                  </button>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </main>
  );
}
